import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  AddressElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeAddressElementChangeEvent,
} from '@stripe/stripe-js';
import logEvent from 'analytics/logEvent';
import { logProcessingFailed } from 'domains/payments/processingFailed';
import cn from 'classnames';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

import Button from 'components/basic/Button';
import { Caption } from 'components/basic/Typography';
import s from 'components/complex/payments/PaymentForm/PaymentForm.module.scss';
import { planPaymentDescription } from 'components/complex/payments/PaymentForm/constants';
import type { LogPaymentArgsType } from 'components/complex/payments/PaymentForm/index';
import PaymentPreviewConfirm from 'components/complex/payments/PaymentForm/PaymentPreviewConfirm';
import { FormattedPrice } from 'components/domains/payments/FormattedPrice';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { ProductType, IntervalType } from 'redux-store/slices/products/types';
import {
  chargeFinish,
  setChargeProcessingStatus,
} from 'redux-store/slices/purchases';
import attachPaymentsMethodThunk from 'redux-store/thunks/paymentsMethods/attachPaymentsMethodThunk';
import chargePurchaseThunk from 'redux-store/thunks/purchases/chargePurchaseThunk';
import stripeInvoicePreviewThunk from 'redux-store/thunks/payments/stripeInvoicePreviewThunk';

import { QueryParser } from 'domains/common/queryParser';
import { getTariff } from 'domains/products/products.utils';
import { resetPreview } from 'redux-store/slices/paymentsPreview';

const style = {
  base: {
    fontSize: '16px',
    fontFamily: 'November',
    color: '#FFFFFF',
    fontWeight: 'normal',
    '::placeholder': {
      color: '#A5A8AD',
    },
  },
  invalid: {
    color: '#ff003f',
  },
};

interface Props {
  selectedPlan: ProductType;
  coupon?: string;
  selectedWalletId: null | string;
  setSelectedWalletId: (id: null | string) => void;
  logClickButton: (arg: LogPaymentArgsType) => void;
}

interface StripeValidationError {
  type: 'validation_error';
  code: string;
  message: string;
}

function getDescriptionWithPrice(
  selectedPlan?: ProductType,
): JSX.Element | null {
  const priceData = selectedPlan?.prices[0];

  if (!priceData) {
    return null;
  }

  const { interval, interval_count, price, currency } = priceData;
  const formattedPrice = (
    <FormattedPrice price={price.toFixed(2)} currency={currency} />
  );

  if (interval === 'lifetime') {
    return (
      <>
        One-time payment. No renewal. Questions?{' '}
        <a href="mailto:support@welltory.com">support@welltory.com</a>
      </>
    );
  }

  if (interval === 'month') {
    const intervalMonths = interval_count && interval_count > 1;
    return (
      <>
        Auto-renews at the regular price unless canceled.
      </>
    );
  }

  if (interval === 'year') {
    return (
      <>
        Auto-renews at the regular price unless canceled.
      </>
    );
  }

  return null;
}

export default function PaymentElements({
  selectedPlan,
  selectedWalletId,
  setSelectedWalletId,
  coupon,
  logClickButton,
}: Props): JSX.Element {
  const elements = useElements();
  const stripe = useStripe();
  const router = useRouter();
  const t = useTranslationAndTypograph();

  const dispatch = useAppDispatch();

  const { region } = useAppSelector(state => state.ui);
  const paymentsPreview = useAppSelector(state => state.paymentsPreview);

  const [stripeCardValues, setStripeCardValues] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
    address: false,
  });

  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [isPreviewRequested, setPreviewRequested] = useState(false);
  // const [isRequestingPreview, setIsRequestingPreview] = useState(false);

  const [stripeValidationError, setStripeValidationError] = useState<
    StripeValidationError | undefined
  >();
  
  const buttonTextQuery = router.query.button_text as string | undefined;
  const compact = router.query.compact as string | undefined;

  const query = new QueryParser(window.location.search);

  const priceIds = useMemo(
    () => selectedPlan.prices.map(price_id => price_id.stripe_price_id),
    [selectedPlan.prices],
  );

  const handleCheckStripeValues = (
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent,
  ): void => {
    const { elementType, complete, error } = event;

    if (error) {
      logEvent('Web_Payment_Validation_Failed', {
        validation_error: error.code,
      });
    }

    setStripeValidationError(error);
    setStripeCardValues(prev => ({
      ...prev,
      [elementType]: complete,
    }));
  };

  const handleCheckAddressElement = (event: StripeAddressElementChangeEvent): void => {
    const { complete, value } = event;

    if (isPreviewRequested) {
      dispatch(resetPreview());
      setPreviewRequested(false);
      // setIsRequestingPreview(false);
      logEvent('Web_Payment_Tax_Preview_Reset_Address_Changed');
    }

    // const hasEmptyField = !value.address.line1 || !value.address.city || !value.address.postal_code || !value.address.state;

    // const error =
    //   hasEmptyField
    //     ? {
    //         type: 'validation_error',
    //         code: 'incomplete_address',
    //         message: 'Please complete the address.',
    //       } as StripeValidationError
    //     : undefined;
  
    // if (error) {
    //   logEvent('Web_Payment_Validation_Failed', {
    //     validation_error: error.code,
    //   });
    // }
  
    // setStripeValidationError(error);
  
    setStripeCardValues(prev => ({
      ...prev,
      address: complete,
    }));
  };

  useEffect(() => {
    setButtonDisabled(selectedWalletId === null);
  }, [selectedWalletId]);

  useEffect(() => {
    (async () => {
      const { cardNumber, cardCvc, cardExpiry, address } = stripeCardValues;

      const isAddressValid = region === 'US' ? address : true;
      // const isAddressValid = address;
      // const isAddressValid = true;
      if (selectedWalletId && !isAddressValid) {
        setButtonDisabled(true);
        return;
      }
      else if (!selectedWalletId && (!cardNumber || !cardCvc || !cardExpiry || !isAddressValid)) {
        setButtonDisabled(true);
        return;
      }
      
      logEvent('Web_Payment_Pay_Button_Unlocked');
      setButtonDisabled(false);
    })();
  }, [stripeCardValues, region, selectedWalletId]);

  useEffect(() => {
    (async () => {
      if (region !== 'US' || !selectedPlan) {
        return;
      }
      const isAddressValid = stripeCardValues.address;
      if (selectedWalletId && !isAddressValid) {
        if (isPreviewRequested) {
          dispatch(resetPreview());
          setPreviewRequested(false);
        }
        return;
      }
      else if (!selectedWalletId && (!stripeCardValues.cardCvc || !stripeCardValues.cardExpiry || !stripeCardValues.cardNumber || !isAddressValid)) {
        if (isPreviewRequested) {
          dispatch(resetPreview());
          setPreviewRequested(false);
        }
        return;
      }

      if (!isPreviewRequested) {
        // if (isRequestingPreview || paymentsPreview.status === 'loading') {
          if (paymentsPreview.status === 'loading') {
            return;
          }
  
          // setIsRequestingPreview(true);
  
          const billingAddressElement = elements?.getElement(AddressElement);
          const addressValue = await billingAddressElement?.getValue();
  
          const addressPayload = addressValue?.value?.address;
  
          if (!addressPayload?.city ||
              !addressPayload?.postal_code || !addressPayload?.state) {
            return;
          }
  
          await dispatch(
            stripeInvoicePreviewThunk({
              price_ids: priceIds,
              coupon,
              ...addressPayload,
            }),
          );
  
          logEvent('Web_Payment_Tax_Preview_Requested');
          setPreviewRequested(true);
          // setIsRequestingPreview(false);
  
          // return;
      }
    })();
  }, [stripeCardValues, selectedWalletId]);

  // eslint-disable-next-line complexity
  async function processPayment(): Promise<unknown> {
    logClickButton({ paymentSystem: 'card' });
    let payment_method_id = null;
    setButtonDisabled(true);

    if (elements && stripe && !selectedWalletId) {
      const cardElement = elements.getElement(CardNumberElement);
      const billingAddressElement = elements.getElement(AddressElement);

      if (!cardElement) return null;

      let billing_details = {};

      if (billingAddressElement) {
        const { value } = await billingAddressElement.getValue();
        billing_details = {
          name: value.name,
          address: {
            line1: value.address.line1,
            line2: value.address.line2,
            city: value.address.city,
            state: value.address.state,
            postal_code: value.address.postal_code,
            country: value.address.country,
          },
        };
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: billing_details,
      });

      logEvent('Web_Payment_New_Card_Created');

      if (error) {
        const stripeError = error.code ?? error.type ?? 'unknown_error';
        logProcessingFailed({
          code: error.code,
          declineCode: error.decline_code,
          type: error.type,
          message: error.message,
        });
        setButtonDisabled(false);
        dispatch(
          setChargeProcessingStatus({
            status: 'fulfilled',
            details: stripeError,
          }),
        );

        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (paymentMethod && !error) {
        const attachResult = await dispatch(
          attachPaymentsMethodThunk({ id: paymentMethod.id }),
        ).unwrap();

        if (attachResult.success === true) {
          payment_method_id = attachResult.result.id;
          logEvent('Web_Payment_New_Card_Attached');
        } 
        else {
          const attachError = attachResult.result?.code ?? 'unknown_error';
          dispatch(
            setChargeProcessingStatus({
              status: 'fulfilled',
              details: attachError,
            }),
          );
          logProcessingFailed({
            code: attachResult.result?.code,
            declineCode: attachResult.result?.decline_code,
            type: attachResult.result?.type,
            message: attachResult.result?.message,
          });
          setButtonDisabled(false);

          return;
        }
      }
    }

    if (selectedWalletId) {
      payment_method_id = selectedWalletId;
    }

    if (!payment_method_id) {
      return;
    }

    const chargeResult = await dispatch(
      chargePurchaseThunk({
        coupon,
        product_id: selectedPlan.id,
        payment_method_id,
        price_ids: priceIds,
        extra: {
          interval: selectedPlan.prices[0].interval,
          tariff: getTariff(selectedPlan),
          discount: Number(selectedPlan.prices[0].discount),
          price: selectedPlan.prices[0].discount_price ?? selectedPlan.prices[0].price,
        },
      }),
    ).unwrap();

    if (
      stripe &&
      elements &&
      chargeResult.result?.status === 'requires_source_action'
    ) {
      try {
        logEvent('Web_Payment_3d_Secure_Redirected');

        // @ts-ignore
        const result = await stripe.confirmPayment({
          clientSecret: chargeResult.result.client_secret,
          confirmParams: {
            return_url: `${window.location.origin}${
              window.location.pathname
            }${query.toString()}`,
          },
          redirect: 'always',
        });

        if (result.error) {
          logEvent('Web_Payment_3d_Secure_Failed');
          dispatch(
            setChargeProcessingStatus({
              status: 'fulfilled',
              details: 'some_error',
            }),
          );

          return;
        }
      } catch (e) {
        dispatch(
          setChargeProcessingStatus({
            status: 'fulfilled',
            details: 'some_error',
          }),
        );

        return;
      }
    }

    dispatch(chargeFinish(chargeResult));

    return;
  }

  useEffect(() => {
    if (region === 'US' && isPreviewRequested && paymentsPreview.status === 'ready') {
      logEvent('Web_Payment_Tax_Preview_Shown');
    }

    if (paymentsPreview.status === 'error') {
      setButtonDisabled(false);
    }
  }, [isPreviewRequested, paymentsPreview, region]);

  const { interval, interval_count } = selectedPlan.prices[0];
  
  let description;
  const intervalKey = `${interval_count}month` as IntervalType;
  if (interval === 'month' && interval_count !== undefined && planPaymentDescription[intervalKey]) {
    description = t(planPaymentDescription[intervalKey]);
  }
  else if (planPaymentDescription[interval]) {
    description = t(planPaymentDescription[interval]);
  }

  if (buttonTextQuery) {
    description =
      getDescriptionWithPrice(selectedPlan) ?? description;
  }

  return (
    <>
      <div className={cn(s.cardWrapper, 'mt-5')}>
        <CardNumberElement
          className={cn(s.numberInput, 'cy-stripe-card-number')}
          onChange={(e): void => handleCheckStripeValues(e)}
          onFocus={(): void => setSelectedWalletId(null)}
          options={{
            showIcon: true,
            style,
          }}
        />
        <div className={s.inputAndCvc}>
          <CardExpiryElement
            className={cn(s.dateInput, 'cy-stripe-expiry')}
            onChange={(e): void => handleCheckStripeValues(e)}
            onFocus={(): void => setSelectedWalletId(null)}
            options={{
              style,
            }}
          />
          <CardCvcElement
            className={cn(s.cvcInput, 'cy-stripe-cvc')}
            onChange={(e): void => handleCheckStripeValues(e)}
            onFocus={(): void => setSelectedWalletId(null)}
            options={{
              style,
            }}
          />
        </div>
      </div>
      {/* {/US|CA/.test(region) && */}
      {/US/.test(region) &&
        <div className={cn(s.cardWrapper, s.cardWrapperAddress, 'mt-5')}>
          <AddressElement
            className={cn(s.billingAddress, 'cy-stripe-billing-address')}
            options={{
              mode: 'billing',
              // allowedCountries: ['US', 'CA'],
              allowedCountries: ['US'],
              autocomplete: {
                mode: 'automatic'
              },
              // defaultValues: {
              //   address: {
              //     country: 'CA'
              //   }
              // }
            }}
            onChange={handleCheckAddressElement}
          />
        </div>
      }
      {stripeValidationError && (
        <Caption className={cn(s.stripeValidationError, 'cy-stripe-error')}>
          {stripeValidationError.message}
        </Caption>
      )}
      {/* {/US|CA/.test(region) && ( */}
      {/US/.test(region) && (
        <PaymentPreviewConfirm
          status={paymentsPreview.status}
          subtotal={paymentsPreview.subtotal}
          tax={paymentsPreview.tax}
          total={paymentsPreview.total}
          currency={paymentsPreview.currency}
          error={paymentsPreview.error}
        />
      )}
      <div className={cn(s.buttonWrapper, 'mt-8')}>
        <Button
          onClick={processPayment}
          className={cn(s.continueButton, 'cy-checkout-button')}
          size="large"
          // disabled={isButtonDisabled || isRequestingPreview}
          disabled={isButtonDisabled}
        >
          {t('pay with this card')}
        </Button>

        {!!compact
          ? null
          : description && (
              <Caption className={s.paymentPlanCaption}>
                {description}
              </Caption>
            )}
      </div>
    </>
  );
}
